import {
  YSTNJ_ASSISTANT_CAPABILITY_MATRIX,
  listAssistantCapabilitiesForContext,
  type AssistantCapabilityCatalogScope,
  type AssistantCapabilityDefinition,
  type AssistantCapabilityType,
  type AssistantModuleKey,
  type AssistantPersona,
} from './capability-matrix';
import {
  buildAssistantPromptMatrixScenarioKey,
  buildAssistantPromptMatrixSourceKey,
  YSTNJ_ASSISTANT_PROMPT_MATRIX_SOURCE_ROWS,
  type AssistantPromptMatrixSourceRow,
} from './prompt-matrix-source';
import {
  normalizeAssistantRouteIdentity,
  routeMatchesAnyAssistantPattern,
  stripAssistantRoutePatternQualifier,
} from './route-matching';
import {
  resolveAssistantRouteFamily,
  type AssistantRouteFamilyId,
} from './route-family-registry';

export type AssistantQueryRequestType =
  | 'summary'
  | 'detail'
  | 'status'
  | 'scheduling'
  | 'timeline'
  | 'documents'
  | 'communication'
  | 'risk'
  | 'next_steps';

export type AssistantLikelySource =
  | 'listAssistantCapabilities'
  | 'previewAssistantAction'
  | 'executeAssistantAction'
  | 'listApplications'
  | 'getApplication'
  | 'listTimelineEvents'
  | 'listCalendarEvents'
  | 'listFamilies'
  | 'getFamily'
  | 'listFamilyDocuments'
  | 'listCommunications'
  | 'getFamilyBalance'
  | 'listFamilyAccountTransactions'
  | 'listFamilyTimelineEvents'
  | 'listFaculty'
  | 'getFaculty'
  | 'listFacultyAssignments'
  | 'listFacultyNotes'
  | 'listFacultyCommunications'
  | 'getAttendanceOverview'
  | 'listAttendanceRecords'
  | 'getAttendanceStudentHistory'
  | 'getAttendanceControlPlaneConfig'
  | 'listAttendanceAuditEvents'
  | 'getAttendanceImportStatus'
  | 'routeFamilyContext'
  | 'semanticRouteCatalog'
  | 'context.faqReference';

export type AssistantPersonaState =
  | 'staff-config'
  | 'staff-review'
  | 'parent-self-service';

export type AssistantPromptMatrixSourceOrigin =
  | 'source-csv'
  | 'capability-fallback';

export type AssistantDataStateSignal =
  | 'baseline'
  | 'pending_invites'
  | 'missing_documents'
  | 'stalled_pipeline'
  | 'overdue_commitments'
  | 'capacity_pressure'
  | 'budget_pressure'
  | 'sibling_divergence'
  | 'medical_accommodations'
  | 'unresolved_flags'
  | 'income_discrepancy'
  | 'committee_readiness';

export type AssistantPromptMatrixSnapshotEntry = {
  id: string;
  page: string;
  tab: string;
  sequence: number;
  title: string;
  prompt: string;
  type: AssistantCapabilityType;
  method: string | null;
  rationale: string;
  module: AssistantModuleKey;
  personas: AssistantPersona[];
  routePrefixes: string[];
  personaState: AssistantPersonaState;
  queryRequestType: AssistantQueryRequestType | null;
  analysisScope: 'dataset' | 'focused';
  likelySources: AssistantLikelySource[];
  sourceOrigin: AssistantPromptMatrixSourceOrigin;
  scenarioKey: string;
  dataStateSignals: AssistantDataStateSignal[];
};

const dedupe = <T,>(values: T[]): T[] => Array.from(new Set(values));

const normalizeToken = (value: string): string =>
  value.trim().toLowerCase().replace(/[\s_]+/g, '-');

const routeMatchesAnyPrefix = (pathname: string, prefixes: string[]): boolean => {
  return routeMatchesAnyAssistantPattern(pathname, prefixes);
};

const routeFamilyMatchesAnyPrefix = (
  routeFamilyId: AssistantRouteFamilyId,
  prefixes: string[],
): boolean => (
  prefixes.some((pattern) => (
    resolveAssistantRouteFamily(stripAssistantRoutePatternQualifier(pattern)).id === routeFamilyId
  ))
);

const normalizePathname = (value: string | null | undefined): string =>
  normalizeAssistantRouteIdentity(value);

const SOURCE_ROW_BY_KEY = new Map<string, AssistantPromptMatrixSourceRow>(
  YSTNJ_ASSISTANT_PROMPT_MATRIX_SOURCE_ROWS.map((row) => [
    buildAssistantPromptMatrixSourceKey({
      page: row.page,
      tab: row.tab,
      sequence: row.sequence,
      prompt: row.prompt,
    }),
    row,
  ] as const)
);

const resolveSourceRow = (
  capability: AssistantCapabilityDefinition
): AssistantPromptMatrixSourceRow | null => {
  const key = buildAssistantPromptMatrixSourceKey({
    page: capability.page,
    tab: capability.tab,
    sequence: capability.sequence,
    prompt: capability.title,
  });
  return SOURCE_ROW_BY_KEY.get(key) ?? null;
};

const inferQueryRequestType = (
  capability: AssistantCapabilityDefinition
): AssistantQueryRequestType | null => {
  if (capability.type === 'action') {
    return null;
  }
  const id = capability.id.toLowerCase();
  const title = capability.title.toLowerCase();
  const prompt = capability.prompt.toLowerCase();
  const queryText = `${id} ${title} ${prompt}`;
  if (queryText.includes('scheduling') || queryText.includes('scheduled')) return 'scheduling';
  if (queryText.includes('timeline') || queryText.includes('time-in-pipeline')) return 'timeline';
  if (queryText.includes('family record') || queryText.includes('household record')) return 'detail';
  if (
    queryText.includes('documents')
    || queryText.includes('missing document')
    || queryText.includes('verify-income-against-documents')
  ) {
    return 'documents';
  }
  if (queryText.includes('communication') || queryText.includes('follow-up') || queryText.includes('outreach')) {
    return 'communication';
  }
  if (
    queryText.includes('risk')
    || queryText.includes('at-risk')
    || queryText.includes('flag-stalled')
    || queryText.includes('flag-severity')
    || queryText.includes('unreported-income')
    || queryText.includes('financial-consistency')
  ) {
    return 'risk';
  }
  if (
    queryText.includes('bottleneck')
    || queryText.includes('conversion')
    || queryText.includes('yield')
    || queryText.includes('capacity')
  ) {
    return 'summary';
  }
  if (
    queryText.includes('overdue-follow-ups')
    || queryText.includes('next-steps')
    || queryText.includes('next action')
    || queryText.includes('next step')
  ) {
    return 'next_steps';
  }
  if (
    queryText.includes('explain-aid-calculation')
    || queryText.includes('compare-tuition-to-ability')
    || queryText.includes('project-multi-year')
    || queryText.includes('benchmark')
    || queryText.includes('ability')
    || queryText.includes('consistency')
  ) {
    return 'detail';
  }
  if (queryText.includes('status')) return 'status';
  if (queryText.includes('detail')) return 'detail';
  return 'summary';
};

const inferAnalysisScope = (
  capability: AssistantCapabilityDefinition,
  source: AssistantPromptMatrixSourceRow | null
): 'dataset' | 'focused' => {
  if (capability.analysisScope) {
    return capability.analysisScope;
  }
  if (capability.type === 'action') {
    return 'focused';
  }
  const page = normalizeToken(source?.page ?? capability.page);
  const tab = normalizeToken(source?.tab ?? capability.tab);
  const isSchoolDashboardInsight = (
    (
      capability.module === 'admissions'
      || capability.module === 'financial-aid'
      || capability.module === 'students-families'
    )
    && page !== 'parent-portal'
    && tab === 'dashboard'
  );
  if (isSchoolDashboardInsight) {
    return 'dataset';
  }
  if (
    capability.module === 'attendance'
    && (
      capability.id === 'attendance-overview-review-absence-trends'
      || capability.id === 'attendance-records-identify-follow-up-students'
    )
  ) {
    return 'dataset';
  }
  return 'focused';
};

const inferPersonaState = (capability: AssistantCapabilityDefinition): AssistantPersonaState => {
  if (capability.personas.includes('parent')) {
    return 'parent-self-service';
  }
  if (capability.module === 'settings') {
    return 'staff-config';
  }
  return 'staff-review';
};

const inferLikelySources = (
  capability: AssistantCapabilityDefinition,
  requestType: AssistantQueryRequestType | null
): AssistantLikelySource[] => {
  if (capability.type === 'action') {
    return ['listAssistantCapabilities', 'previewAssistantAction', 'executeAssistantAction'];
  }

  if (capability.module === 'students-families') {
    switch (capability.id) {
      case 'students-families-dashboard-guide-add-student-record':
      case 'students-families-dashboard-guide-add-family-record':
      case 'students-families-dashboard-choose-roster-view':
        return ['routeFamilyContext', 'semanticRouteCatalog'];
      case 'students-families-dashboard-identify-follow-up-families':
        return ['listFamilies', 'listCommunications', 'listFamilyTimelineEvents'];
      case 'students-families-dashboard-summarize-balance-risk':
        return ['listFamilies', 'getFamilyBalance', 'listFamilyAccountTransactions'];
      case 'students-families-children-review-status-contact-and-schedule':
      case 'students-families-family-summarize-profile':
        return ['getFamily', 'listCommunications', 'listFamilyTimelineEvents'];
      case 'students-families-tuition-review-balance-and-payment-methods':
        return ['getFamily', 'getFamilyBalance', 'listFamilyAccountTransactions'];
      case 'students-families-documents-review-missing-documents-and-follow-up':
        return ['getFamily', 'listFamilyDocuments', 'listFamilyTimelineEvents'];
      case 'students-families-notes-summarize-activity-timeline':
        return ['getFamily', 'listCommunications', 'listFamilyTimelineEvents'];
      default:
        return ['listFamilies', 'getFamily', 'listFamilyTimelineEvents'];
    }
  }

  if (capability.module === 'faculty') {
    switch (capability.id) {
      case 'faculty-directory-summarize-staffing-coverage':
        return ['listFaculty'];
      case 'faculty-directory-guide-add-faculty-record':
      case 'faculty-profile-guide-edit-profile':
      case 'faculty-notes-guide-add-note':
      case 'faculty-communications-guide-compose-communication':
        return ['routeFamilyContext', 'semanticRouteCatalog'];
      case 'faculty-profile-review-profile-summary':
        return ['getFaculty'];
      case 'faculty-teaching-review-assignment':
        return ['getFaculty', 'listFacultyAssignments'];
      case 'faculty-notes-summarize-notes':
        return ['getFaculty', 'listFacultyNotes'];
      case 'faculty-communications-review-communications':
        return ['getFaculty', 'listFacultyCommunications'];
      default:
        return ['routeFamilyContext', 'semanticRouteCatalog'];
    }
  }

  if (capability.module === 'attendance') {
    switch (capability.id) {
      case 'attendance-overview-review-absence-trends':
        return ['getAttendanceOverview', 'listAttendanceRecords', 'getAttendanceStudentHistory'];
      case 'attendance-records-identify-follow-up-students':
        return ['listAttendanceRecords', 'getAttendanceStudentHistory', 'routeFamilyContext'];
      case 'attendance-teacher-view-review-preview':
        return ['listAttendanceRecords', 'routeFamilyContext'];
      case 'attendance-student-history-review-read-only':
        return ['getAttendanceStudentHistory', 'listAttendanceRecords'];
      case 'attendance-control-plane-review-config-audit':
        return ['getAttendanceControlPlaneConfig', 'getAttendanceImportStatus', 'listAttendanceAuditEvents'];
      default:
        return ['listAttendanceRecords', 'routeFamilyContext'];
    }
  }

  if (capability.module === 'general') {
    return ['routeFamilyContext', 'semanticRouteCatalog'];
  }

  const sources: AssistantLikelySource[] = ['listApplications', 'getApplication'];
  switch (requestType) {
    case 'scheduling':
      sources.push('listTimelineEvents', 'listCalendarEvents');
      break;
    case 'timeline':
      sources.push('listTimelineEvents');
      break;
    case 'documents':
    case 'communication':
    case 'next_steps':
      sources.push('listTimelineEvents');
      break;
    case 'risk':
    case 'detail':
      sources.push('listTimelineEvents', 'listCalendarEvents');
      break;
    case 'summary':
    case 'status':
    default:
      sources.push('listTimelineEvents', 'context.faqReference');
      break;
  }
  return dedupe(sources);
};

const inferDataStateSignals = (
  capability: AssistantCapabilityDefinition,
  source: AssistantPromptMatrixSourceRow | null
): AssistantDataStateSignal[] => {
  const text = [
    capability.id,
    capability.title,
    capability.prompt,
    source?.method ?? '',
    source?.rationale ?? '',
  ].join(' ').toLowerCase();
  const signals: AssistantDataStateSignal[] = [];
  if (text.includes('pending invite') || text.includes('invited')) signals.push('pending_invites');
  if (text.includes('missing document') || text.includes('required document')) signals.push('missing_documents');
  if (text.includes('balance risk') || text.includes('overdue balance')) signals.push('budget_pressure');
  if (text.includes('stalled') || text.includes('bottleneck')) signals.push('stalled_pipeline');
  if (text.includes('overdue') || text.includes('past-due')) signals.push('overdue_commitments');
  if (text.includes('capacity')) signals.push('capacity_pressure');
  if (text.includes('budget') || text.includes('threshold')) signals.push('budget_pressure');
  if (text.includes('sibling')) signals.push('sibling_divergence');
  if (text.includes('medical') || text.includes('accommodation') || text.includes('allergy')) {
    signals.push('medical_accommodations');
  }
  if (text.includes('flag') && text.includes('unresolved')) signals.push('unresolved_flags');
  if (text.includes('income') && text.includes('discretionary')) signals.push('income_discrepancy');
  if (text.includes('committee') || text.includes('readiness')) signals.push('committee_readiness');
  if (signals.length === 0) signals.push('baseline');
  return dedupe(signals);
};

const toSnapshotEntry = (
  capability: AssistantCapabilityDefinition
): AssistantPromptMatrixSnapshotEntry => {
  const sourceRow = resolveSourceRow(capability);
  const queryRequestType = inferQueryRequestType(capability);
  const page = sourceRow?.page ?? capability.page;
  const tab = sourceRow?.tab ?? capability.tab;
  const title = sourceRow?.prompt ?? capability.title;
  return {
    id: capability.id,
    page,
    tab,
    sequence: capability.sequence,
    title,
    prompt: capability.prompt,
    type: capability.type,
    method: sourceRow?.method ?? capability.method ?? null,
    rationale: sourceRow?.rationale ?? capability.rationale,
    module: capability.module,
    personas: capability.personas,
    routePrefixes: capability.routePrefixes,
    personaState: inferPersonaState(capability),
    queryRequestType,
    analysisScope: inferAnalysisScope(capability, sourceRow),
    likelySources: inferLikelySources(capability, queryRequestType),
    sourceOrigin: sourceRow ? 'source-csv' : 'capability-fallback',
    scenarioKey: buildAssistantPromptMatrixScenarioKey({
      page,
      tab,
    }),
    dataStateSignals: inferDataStateSignals(capability, sourceRow),
  };
};

export const YSTNJ_ASSISTANT_PROMPT_MATRIX_SNAPSHOT: AssistantPromptMatrixSnapshotEntry[] =
  YSTNJ_ASSISTANT_CAPABILITY_MATRIX.map(toSnapshotEntry);

const SNAPSHOT_BY_ID = new Map(
  YSTNJ_ASSISTANT_PROMPT_MATRIX_SNAPSHOT.map((entry) => [entry.id, entry] as const)
);

export const listAssistantPromptMatrixSnapshotForContext = (input: {
  pathname?: string | null;
  persona?: string | null;
  module?: AssistantModuleKey | null;
  types?: AssistantCapabilityType[];
  tab?: string | null;
  personaState?: AssistantPersonaState | null;
  dataStateSignals?: AssistantDataStateSignal[];
  strictRouteMatch?: boolean;
  catalogScope?: AssistantCapabilityCatalogScope;
}): AssistantPromptMatrixSnapshotEntry[] => {
  const tabFilter = normalizeToken(String(input.tab ?? ''));
  const personaStateFilter = input.personaState ?? null;
  const dataStateSignals = new Set(input.dataStateSignals ?? []);
  const pathname = normalizePathname(input.pathname);
  const routeFamilyId = resolveAssistantRouteFamily(pathname).id;
  const catalogScope = input.catalogScope ?? 'route';
  const capabilities = listAssistantCapabilitiesForContext(input);
  const entries = capabilities
    .map((capability) => SNAPSHOT_BY_ID.get(capability.id) ?? toSnapshotEntry(capability));

  if (!tabFilter && !personaStateFilter && dataStateSignals.size === 0 && catalogScope === 'route') {
    return entries;
  }

  return entries
    .map((entry) => {
      let score = 0;
      if (routeMatchesAnyPrefix(pathname, entry.routePrefixes)) score += 40;
      if (routeFamilyMatchesAnyPrefix(routeFamilyId, entry.routePrefixes)) score += 10;
      if (tabFilter && normalizeToken(entry.tab) === tabFilter) score += 100;
      if (personaStateFilter && entry.personaState === personaStateFilter) score += 80;
      for (const signal of entry.dataStateSignals) {
        if (dataStateSignals.has(signal)) {
          score += 20;
        }
      }
      return { entry, score };
    })
    .sort((left, right) =>
      right.score - left.score
      || left.entry.sequence - right.entry.sequence
      || left.entry.title.localeCompare(right.entry.title))
    .map(({ entry }) => entry);
};
